const cartDrawerLineWidgetSelectors = [
  "cart-recommendations-component",
  "foxkit-incart-upsell",
];

function preserveCartDrawerLineWidgets(container) {
  const preserved = new Map();

  container
    .querySelectorAll(".f-cart-drawer__item[data-cart-item]")
    .forEach((line) => {
      const widgets = [];

      cartDrawerLineWidgetSelectors.forEach((selector) => {
        const widget = line.querySelector(selector);
        if (!widget) return;

        if (
          selector === "cart-recommendations-component" &&
          !widget.innerHTML.trim()
        ) {
          return;
        }

        widgets.push({ selector, node: widget });
      });

      if (!widgets.length) return;

      widgets.forEach(({ node }) => node.remove());
      preserved.set(line.dataset.cartItem, widgets);
    });

  return preserved;
}

function restoreCartDrawerLineWidgets(container, preserved) {
  container
    .querySelectorAll(".f-cart-drawer__item[data-cart-item]")
    .forEach((line) => {
      const widgets = preserved.get(line.dataset.cartItem);
      if (!widgets) return;

      widgets.forEach(({ selector, node }) => {
        const placeholder = line.querySelector(selector);
        if (placeholder) placeholder.replaceWith(node);
      });
    });
}

function getCartDrawerComponent() {
  return document.getElementById("Drawer-Cart");
}

function cartDrawerShouldStayOpen(event) {
  const drawer = getCartDrawerComponent();
  return Boolean(
    event?.detail?.open === true ||
      drawer?.isOpen?.() ||
      drawer?.hasAttribute("open") ||
      window.FoxThemeCartShipping?.pendingError ||
      document.body.classList.contains("prevent-scroll")
  );
}

function keepCartDrawerOpen(drawer) {
  if (!drawer) return;
  drawer.removeAttribute("hidden");
  if (typeof drawer.openDrawer === "function") {
    if (!drawer.isOpen?.()) drawer.openDrawer();
    else drawer.setAttribute("open", "");
  } else {
    drawer.setAttribute("open", "");
  }
  document.body.classList.add("prevent-scroll");
}

function replaceCartDrawerSectionHtml(sectionElement, html) {
  if (
    sectionElement.matches(".f-cart-drawer__items") ||
    sectionElement.classList.contains("f-cart-drawer__items")
  ) {
    const preserved = preserveCartDrawerLineWidgets(sectionElement);
    sectionElement.innerHTML = html;
    restoreCartDrawerLineWidgets(sectionElement, preserved);
    return;
  }

  sectionElement.innerHTML = html;
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.FoxTheme = window.FoxTheme || {};
    window.FoxTheme.Cart = this;
    this.drawer = this.closest("drawer-component");
    window.FoxTheme.CartDrawer = this.drawer;
    this.openWhenAdded = this.dataset.openWhenAdded === "true";
    this.refreshGeneration = 0;

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate
    );
    document.addEventListener("cart:refresh", this.onCartRefreshListener);
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
  }

  onCartUpdate(state) {
    if (state.item_count !== 0 && !state.cartItemsRendered) {
      this.renderContents(
        state,
        true,
        state.source === "quick-order-list" ? false : true
      );
    }
  }

  async onCartRefresh(event) {
    const cartRoot = document.getElementById("cart-drawer");
    if (!cartRoot) return;

    const keepOpen = cartDrawerShouldStayOpen(event);
    const generation = ++this.refreshGeneration;

    try {
      const response = await fetch(
        `${FoxThemeSettings.routes.root}?section_id=cart-drawer`
      );
      const responseText = await response.text();
      if (generation !== this.refreshGeneration) return;

      const parsedHTML = new DOMParser().parseFromString(
        responseText,
        "text/html"
      );
      const nextRoot = parsedHTML.getElementById("cart-drawer");
      if (!nextRoot) return;

      if (keepOpen) {
        this.replaceOpenDrawerContents(nextRoot);
        keepCartDrawerOpen(getCartDrawerComponent());
      } else {
        cartRoot.innerHTML = nextRoot.innerHTML;
      }

      FoxThemeCartHelpers.getState().then((state) => {
        FoxThemeCartHelpers.updateCartCount(state.item_count);
      });
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  replaceOpenDrawerContents(nextRoot) {
    const skipFooter = Boolean(window.FoxThemeCartShipping?.pendingError);
    this.getSectionsToRender(true).forEach((section) => {
      if (skipFooter && section.selector === ".f-drawer__footer") return;
      const current = document.querySelector(section.selector);
      const next = nextRoot.querySelector(section.selector);
      if (current && next) {
        replaceCartDrawerSectionHtml(current, next.innerHTML);
      }
    });
    if (!skipFooter) window.FoxThemeCartShipping?.showPendingError?.();
  }

  renderContents(parsedState, renderFooter = true, isOpen = true) {
    if (parsedState.errors) return;
    this.classList.contains("is-empty") && this.classList.remove("is-empty");
    const updateFooter =
      renderFooter && !window.FoxThemeCartShipping?.pendingError;
    this.getSectionsToRender(updateFooter).forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      replaceCartDrawerSectionHtml(
        sectionElement,
        this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        )
      );
    });

    setTimeout(() => {
      if (!window.FoxThemeCartShipping?.pendingError) {
        window.FoxThemeCartShipping?.showPendingError?.();
      }
      const drawer = this.drawer || getCartDrawerComponent();
      if (!drawer) return;
      if (window.FoxThemeCartShipping?.pendingError) {
        keepCartDrawerOpen(drawer);
        return;
      }
      if (this.openWhenAdded && isOpen) {
        keepCartDrawerOpen(drawer);
        window.FoxThemeEvents.emit(`ON_CART_DRAWER_OPEN`, parsedState);
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender(renderFooter = false) {
    const sections = [
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
    ];

    if (renderFooter) {
      sections.push({
        id: "cart-drawer",
        selector: ".f-drawer__footer",
      });
    }

    return sections;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  initLoading() {
    this.loading = new window.FoxTheme.AnimateLoading(
      document.querySelector("cart-drawer"),
      { overlay: document.querySelector("cart-drawer") }
    );
  }

  renderContents(parsedState) {
    if (parsedState.errors) return;

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      if (!elementToReplace) return;

      replaceCartDrawerSectionHtml(
        elementToReplace,
        this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        )
      );

      const cartFooter = document.getElementById("main-cart-footer");
      this.classList.toggle("is-empty", parsedState.item_count === 0);
      if (cartFooter) {
        cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
      }
    });
  }

  getSectionsToRender() {
    return [
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__block-subtotal",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
